import React, { useEffect, useRef, useState } from 'react'
import Tooltip from '../common/Tooltip'
import LiveTile from '../common/LiveTile'
import { NavLink, useHistory } from 'react-router-dom'
import AlertsUpdates from '../alertsupdates/AlertsUpdates'
import { UserProfile, UserProfileFormValues } from '../../models/Profile'
import '../common/WorkplaceFooter.css'
import WorkplaceFooter from '../common/WorkplaceFooter'
import Protected from '../Protected'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import FormErrorDisplay from '../common/FormErrorMessages'
import Conditional from '../common/Conditional'
import ProfileService from '../../services/ProfileService'
import useErrorHandler from '../../hooks/useErrorHandler'
import axios from 'axios'
import useGovCMSFilterCookie from '../../hooks/useGovCMSFilterCookie'
import { FormDependencyLoaderProvider } from '../context/FormDependencyLoaderContext'
import { NavHashLink } from 'react-router-hash-link'
import { PATHS } from '../Routes'

const HomePage = () => {
  const { instance, inProgress } = useMsal()
  const mountedRef = useRef(false)
  const [isLoading, setLoading] = useState(true)
  const [userErrors, , handleErrors] = useErrorHandler()
  const [setIndustryCookie, setSubIndustryCookie] = useGovCMSFilterCookie()
  const [userProfile, setUserProfile] = useState<UserProfile>(
    new UserProfileFormValues()
  )
  const history = useHistory()
  const activeAccount = instance.getActiveAccount()

  useEffect(() => {
    mountedRef.current = true
    if (activeAccount && inProgress === InteractionStatus.None) {
      const source = axios.CancelToken.source()
      ProfileService.getCurrentUserProfile(source)
        .then((response) => response.result)
        .then((result) => {
          if (mountedRef.current) {
            if (!result || !result.crn || result.requiresRegistration) {
              history.push(PATHS.Register)
              return
            }
            if (result) {
              setUserProfile(result)
              setIndustryCookie(result.govCMSIndustryFilterId)
              setSubIndustryCookie(result.govCMSSubIndustryFilterId)
            }
          }
        })
        .catch((error) => {
          handleErrors(error)
        })
        .finally(() => {
          if (mountedRef.current) {
            setLoading(false)
          }
        })
    }

    return () => {
      mountedRef.current = false
    }
  }, [inProgress])

  return (
    <Protected>
      <Conditional if={!isLoading}>
        <FormErrorDisplay
          show={userErrors?.length > 0}
          errorsList={userErrors}
        />

        <FormDependencyLoaderProvider>
          <div className="content-wrapper">
            <article className="main">
              <p></p>
              <h1>My account</h1>
              <Conditional
                if={userProfile.firstName != null && userProfile.firstName}
              >
                <p id="login-info">
                  Welcome, {userProfile.firstName} CRN: {userProfile.crn}
                  <Tooltip>
                    We use your customer reference number to identify you when
                    you contact us.
                  </Tooltip>
                </p>
              </Conditional>
              <p></p>
              <div className="paragraph paragraph--type--link-cards-home-page live-tiles is-centered">
                <LiveTile
                  to="/my-account/enquiries"
                  name="Enquiries"
                  icon="enquiries"
                >
                  <ul>
                    <li>
                      <NavHashLink
                        to="/my-account/enquiries#new-enquiry"
                        scroll={(el) =>
                          el.scrollIntoView({
                            behavior: 'auto',
                            block: 'start',
                          })
                        }
                      >
                        Submit a new enquiry
                      </NavHashLink>
                    </li>
                    <li>
                      <NavLink to="/my-account/enquiries">
                        See enquiries
                      </NavLink>
                    </li>
                  </ul>
                </LiveTile>
                <LiveTile
                  to="/my-account/resources"
                  name="Resources"
                  icon="resources"
                >
                  <ul>
                    <li>
                      <NavHashLink
                        to="/my-account/resources#onlineLearning"
                        scroll={(el) =>
                          el.scrollIntoView({
                            behavior: 'instant',
                            block: 'end',
                          })
                        }
                      >
                        Online learning
                      </NavHashLink>
                    </li>

                    <Conditional if={userProfile.resourceUserType != 6}>
                      <li>
                        <NavHashLink
                          to="/my-account/resources#templates"
                          scroll={(el) =>
                            el.scrollIntoView({
                              behavior: 'instant',
                              block: 'end',
                            })
                          }
                        >
                          Templates
                        </NavHashLink>
                      </li>
                    </Conditional>
                    <Conditional
                      if={
                        userProfile.resourceUserType === 0 ||
                        userProfile.resourceUserType === 1 ||
                        userProfile.resourceUserType === 2
                      }
                    >
                      <li>
                        <NavHashLink
                          to="/my-account/resources#bestPracticeGuides"
                          scroll={(el) =>
                            el.scrollIntoView({
                              behavior: 'instant',
                              block: 'end',
                            })
                          }
                        >
                          Best practice guides
                        </NavHashLink>
                      </li>
                    </Conditional>
                    <Conditional
                      if={
                        userProfile.resourceUserType === 3 ||
                        userProfile.resourceUserType === 4 ||
                        userProfile.resourceUserType === 5 ||
                        userProfile.resourceUserType === 6
                      }
                    >
                      <li>
                        <NavHashLink
                          to="/my-account/resources#workplaceProblems"
                          scroll={(el) =>
                            el.scrollIntoView({
                              behavior: 'instant',
                              block: 'end',
                            })
                          }
                        >
                          Common workplace problems
                        </NavHashLink>
                      </li>
                    </Conditional>
                  </ul>
                </LiveTile>

                <LiveTile
                  to="/my-account/bookmarks"
                  name="Bookmarks"
                  icon="bookmarks"
                >
                  <ul>
                    <li>
                      <NavLink to="/my-account/bookmarked-awards">
                        Awards
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/my-account/bookmarked-results">
                        Results
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/my-account/bookmarked-pages">Pages</NavLink>
                    </li>
                  </ul>
                </LiveTile>
              </div>
              <AlertsUpdates handleErrors={handleErrors} />
            </article>
          </div>
        </FormDependencyLoaderProvider>

        <WorkplaceFooter userProfile={userProfile} isLoading={isLoading} />
      </Conditional>
    </Protected>
  )
}
export default HomePage
